import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { RequirePermission } from '@klareospace/pharos-lib-front-utils';
import type { Task } from '@klareospace/pharos-lib-types';
import { Dialog, DialogContent, DialogHeader, DialogTitle, ScrollArea } from '@klareospace/pharos-lib-web-utils';

import { useTaskHistory } from '../hooks/use-task-history';
import { TaskCarrierDriverSection } from './task-carrier-driver-section';
import { TaskDetailHeader } from './task-detail-header';
import { TaskHistorySection } from './task-history-section';
import { TaskPickupDeliveryTimeline } from './task-pickup-delivery-timeline';
import { TaskProofsSection } from './task-proofs-section';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TaskDetailDialogProps {
  readonly onClose: () => void;
  /** Called with the fresh Task after a successful assignment, to refresh the dialog immediately. */
  readonly onTaskUpdated: (task: Task) => void;
  readonly open: boolean;
  /** The task row data (from `useOrderTasks`, already loaded by the order drawer). */
  readonly task: Task | null;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Task Detail modal opened from a task row in `TasksSection`. Rendered as a sibling of `OrderDetailDrawer`'s
 * `SideDrawer` — a centered `Dialog`, not a second side sheet, with its own independent open state.
 * Guarded defensively by `orders:list` (the modal is only reachable from the already-gated orders list flow).
 */
export function TaskDetailDialog({ onClose, onTaskUpdated, open, task }: TaskDetailDialogProps): React.JSX.Element {
  const { t } = useTranslation('tasks');
  const titleRef = React.useRef<HTMLHeadingElement>(null);

  const taskId = task?.id ?? '';
  const {
    data: historyPage,
    isError: isHistoryError,
    isLoading: isHistoryLoading,
    refetch: refetchHistory,
  } = useTaskHistory(taskId, { enabled: open && taskId.length > 0 });
  const history = historyPage?.items;

  function handleOpenChange(nextOpen: boolean): void {
    if (!nextOpen) {
      onClose();
    }
  }

  /** Redirects Radix's default open-autofocus away from the copyable task-id control (avoids an unwanted tooltip). */
  function handleOpenAutoFocus(event: Event): void {
    event.preventDefault();
    titleRef.current?.focus();
  }

  return (
    <RequirePermission permission="orders:list">
      <Dialog onOpenChange={handleOpenChange} open={open}>
        <DialogContent
          className="flex max-h-[90vh] max-w-2xl flex-col gap-0 overflow-hidden p-0"
          onOpenAutoFocus={handleOpenAutoFocus}
        >
          {task !== null && (
            <>
              <DialogHeader className="border-b border-border px-6 pb-4 pt-5">
                <DialogTitle className="sr-only" ref={titleRef} tabIndex={-1}>
                  {task.id}
                </DialogTitle>
                <TaskDetailHeader
                  copiedTooltip={t('orders:drawer.copied')}
                  copyTooltip={t('orders:drawer.copy')}
                  task={task}
                />
              </DialogHeader>
              <ScrollArea className="flex-1 overflow-y-auto">
                <div className="space-y-6 px-6 py-5">
                  <TaskPickupDeliveryTimeline task={task} />
                  <TaskCarrierDriverSection onTaskUpdated={onTaskUpdated} task={task} />
                  <TaskHistorySection
                    history={history}
                    isError={isHistoryError}
                    isLoading={isHistoryLoading}
                    onRetry={(): void => void refetchHistory()}
                  />
                  <TaskProofsSection task={task} />
                </div>
              </ScrollArea>
            </>
          )}
        </DialogContent>
      </Dialog>
    </RequirePermission>
  );
}
